/**
 * K3 (round 2, D3) — the selection filter of /strategic-kpis: which values the three lists
 * offer, which persons a selection leaves, and how a selection lives in the URL.
 *
 * LIST logic only. The figures of a selection — the pooled SELECTION node, an exactly matched
 * node, partial teams — are the engine's (`assembleActuals`); the page never re-pools. The rule
 * D3 is applied here only to offer options that fit the other lists ("cascading") and to show
 * the matching rows on the Targets tab; it is the same rule as the engine's `matchesSelection`
 * (OR within a list, AND between lists, inside the BU), and both are tested with the same cases.
 */
import type { KpiPersonYear, KpiSelection } from '../types/strategicKpis';

export const EMPTY_SELECTION: KpiSelection = { companies: [], teams: [], persons: [] };

export type SelectionList = keyof KpiSelection;

export function isEmptySelection(s: KpiSelection | null | undefined): boolean {
    return !s || (s.companies.length === 0 && s.teams.length === 0 && s.persons.length === 0);
}

/** Deduplicated and sorted — the form the URL, the cache key and the comparison use. */
export function canonicalSelection(s: KpiSelection): KpiSelection {
    const c = (xs: string[]) => Array.from(new Set(xs.filter(Boolean))).sort();
    return { companies: c(s.companies), teams: c(s.teams), persons: c(s.persons) };
}

export function selectionKey(s: KpiSelection): string {
    const c = canonicalSelection(s);
    return `c=${c.companies.join(',')};t=${c.teams.join(',')};p=${c.persons.join(',')}`;
}

type Selectable = Pick<KpiPersonYear, 'StaffID' | 'CompanyCode' | 'TeamManagerStaffID' | 'BUCode'>;

/** D3 for one person; `except` ignores one list (the list whose options are being offered). */
export function matchesSelection(p: Selectable, s: KpiSelection, except?: SelectionList): boolean {
    return (except === 'companies' || s.companies.length === 0 || s.companies.includes(p.CompanyCode))
        && (except === 'teams' || s.teams.length === 0 || (p.TeamManagerStaffID !== null && s.teams.includes(p.TeamManagerStaffID)))
        && (except === 'persons' || s.persons.length === 0 || s.persons.includes(p.StaffID));
}

/** The persons of the BUs a selection leaves. */
export function filterBySelection<T extends Selectable>(persons: T[], bus: string[], s: KpiSelection): T[] {
    return persons.filter(p => p.BUCode !== null && bus.includes(p.BUCode) && matchesSelection(p, s));
}

export interface SelectOption { id: string; name: string }
export interface SelectionOptions { companies: SelectOption[]; teams: SelectOption[]; persons: SelectOption[] }

/**
 * The options of each list: the values held by persons of the BUs who match the OTHER two lists,
 * plus whatever is already chosen in this list (so it can be removed). Choosing any offered value
 * therefore never empties the selection. Team names come from the whole year's persons — a
 * manager may sit in another BU (Briatico → Zumwald, F&P).
 */
export function selectionOptions(yearPersons: KpiPersonYear[], bus: string[], s: KpiSelection): SelectionOptions {
    const inBu = yearPersons.filter(p => p.BUCode !== null && bus.includes(p.BUCode));
    const nameOf = new Map(yearPersons.map(p => [p.StaffID, p.FullName]));
    const byName = (a: SelectOption, b: SelectOption) => a.name.localeCompare(b.name, 'de-CH');

    const companyIds = new Set(s.companies);
    for (const p of inBu) if (matchesSelection(p, s, 'companies')) companyIds.add(p.CompanyCode);
    const teamIds = new Set(s.teams);
    for (const p of inBu) if (p.TeamManagerStaffID && matchesSelection(p, s, 'teams')) teamIds.add(p.TeamManagerStaffID);
    const personIds = new Set(s.persons);
    for (const p of inBu) if (matchesSelection(p, s, 'persons')) personIds.add(p.StaffID);

    const personById = new Map(inBu.map(p => [p.StaffID, p]));
    return {
        companies: [...companyIds].map(id => ({ id, name: id })).sort(byName),
        teams: [...teamIds].map(id => ({ id, name: nameOf.get(id) ?? id })).sort(byName),
        persons: [...personIds].map(id => {
            const p = personById.get(id);
            return { id, name: p ? `${p.FullName} · ${p.CompanyCode}${p.IsActive ? '' : ' (left)'}` : id };
        }).sort(byName),
    };
}

/**
 * Drops every value no person of the BUs holds (a year or BU switch, a stale link) — the engine
 * would refuse it (400). Returns the same object when nothing is dropped.
 */
export function pruneSelection(yearPersons: KpiPersonYear[], bus: string[], s: KpiSelection): KpiSelection {
    const inBu = yearPersons.filter(p => p.BUCode !== null && bus.includes(p.BUCode));
    const companies = new Set(inBu.map(p => p.CompanyCode));
    const teams = new Set(inBu.map(p => p.TeamManagerStaffID).filter((x): x is string => !!x));
    const persons = new Set(inBu.map(p => p.StaffID));
    const next = { companies: s.companies.filter(c => companies.has(c)), teams: s.teams.filter(t => teams.has(t)), persons: s.persons.filter(p => persons.has(p)) };
    return next.companies.length === s.companies.length && next.teams.length === s.teams.length && next.persons.length === s.persons.length ? s : next;
}

// ---------------------------------------------------------------------------------------------
// URL — a filtered view is a shareable link (`?bu=SPC&companies=dit,dsp&teams=815008`).
// ---------------------------------------------------------------------------------------------

const LIST_KEYS: SelectionList[] = ['companies', 'teams', 'persons'];

export function readSelection(params: URLSearchParams): KpiSelection {
    const read = (k: string) => (params.get(k) ?? '').split(',').map(x => x.trim()).filter(Boolean);
    return canonicalSelection({ companies: read('companies').map(c => c.toLowerCase()), teams: read('teams'), persons: read('persons') });
}

/** Writes the lists into `params` (empty lists are removed, so an unfiltered page keeps a bare URL). */
export function writeSelection(params: URLSearchParams, s: KpiSelection): URLSearchParams {
    const c = canonicalSelection(s);
    for (const k of LIST_KEYS) {
        if (c[k].length) params.set(k, c[k].join(','));
        else params.delete(k);
    }
    return params;
}
